import React, { useState, useEffect, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Drawer, Spin, Result, Button, Tree, message, Radio } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import styles from "./index.module.scss";
import PageTabs from "../../../components/PageTabs";
import Upload from "../../../components/Upload";
import FormItem from "../components/FormItem";
import { formatTimestamp } from "../../../utils/format";
import { setCookie } from "../../../utils/cookie";
import useQuery from "../../../hooks/useQuery";

import request from "../../../network/request";
import { IConfigItem, IConfigRes } from "../../../network/models/ConfigType";
import { IRecord, Iattach } from "../../../network/models/RecordType";
import { ICacheRes } from "../../../network/models/CacheTypes";
import notAllowMove from "./../../../utils/scroll-lock";

const CACHE_DELAY = 5000;
const FILE_MAX_MB = 10;
const FILE_MAX_SIZE = 1024 * 1024 * FILE_MAX_MB;
const ALLOW_FILE_TYPE = "jpg, jpeg, png, txt, zip, rar, mp4";
const ALLOW_FILE_NUMS = 10;
const MAX_SERVICE_TYPE_NUM = 5;

const initialRecord = {
  solutionDesc: "",
  problemSolved: "",
  remoteDiagnosisRes: 0,
  tid: "",
  uid: "",
  name: "",
  mobile: "",
  vin: "",
  plateNo: "",
  vehicleModel: "",
  vehicleSeries: "",
  vehicleVersion: "",
  serviceId: "",
  type: "",
  typeLabel: "",
  status: 0,
  remark: "",
  attachments: [],
};

export default function RecordPage() {
  const history = useHistory();
  const {
    register,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const [showServiceItemPicker, setShowServiceItemPicker] =
    useState<boolean>(false);
  const [initLoading, setInitLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [commitLoading, setCommitLoading] = useState<boolean>(false);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);

  // 枚举值
  const [serviceType, setServiceType] = useState<any[]>([]);
  const [serviceStatus, setServiceStatus] = useState<IConfigItem[]>([]);

  // 静态展示数据: 服务状态及开始时间
  const [statusShow, setStatusShow] = useState<any>(null);
  const [timeShow, setTimeShow] = useState<any>(null);

  // 记录初始信息
  const recordRef = useRef<IRecord>(initialRecord);

  // 附件
  const attachListRef = useRef<Iattach[]>([]);
  const [attachList, setAttachList] = useState<Iattach[]>([]);

  // 服务类型
  const [selectServiceTypeList, setSelectServiceTypeList] = useState<any>([]);
  const [checkedServiceTypeList, setCheckedServiceTypeList] = useState<any>([]);

  // 问题已解决
  const [problemSolved, setProblemSolved] = useState("");
  // 远程诊断结果
  const [remoteDiagnosis, setRemoteDiagnosis] = useState(0);

  const typeRef = useRef<any>("");
  const typeLabelRef = useRef<any>("");
  const problemSolvedRef = useRef<any>(""); //用于缓存问题是否已解决

  const cacheTimerRef = useRef(0);

  // 从 URL 获取群ID(tid)参数
  const useQueryIns = useQuery();
  const tid = useQueryIns.get("tid");
  const xiaolongToken = useQueryIns.get("xiaolong_token") || null;

  const watchServiceId = watch("serviceId", "");
  const watchRemark = watch("remark", "");
  const watchSolutionDesc = watch("solutionDesc", "");

  useEffect(() => {
    // web 大骁龙通过 URL 参数传递登录态信息
    // 前端需自动写 cookie
    if (xiaolongToken) {
      const [cookieKey, cookieVal] = xiaolongToken.split("=");
      if (cookieKey && cookieVal) {
        setCookie(cookieKey, cookieVal, 7);
      }
    }

    getPageData();

    return () => {
      if (cacheTimerRef.current) {
        window.clearInterval(cacheTimerRef.current);
      }
    };
  }, []);
  problemSolvedRef.current = useMemo(() => {
    return problemSolved;
  }, [problemSolved]);

  const getPageData = () => {
    setInitLoading(true);
    getRecordData();
    getConfigData();
  };

  const getConfigData = async () => {
    try {
      const { serviceType, serviceStatus }: IConfigRes = await request.get(
        "/dragon/service/selectorOptions/list"
      );

      const serviceTypeData: IConfigItem[] = handleServiceType(serviceType);

      setServiceType(serviceTypeData);
      setServiceStatus(serviceStatus);
    } catch (err) {
      console.log(222, err);
      setError(true);
    }
  };

  /**
   * 为servicetype的每一项添加type和typeLable
   * @param serType 服务类型数据
   * @param parentType 父节点的type
   * @param parentTypeLable 父节点的typeLable
   * @returns
   */
  const handleServiceType = (
    serType,
    parentType = [],
    parentTypeLabel = ""
  ): IConfigItem[] => {
    if (serType === null) {
      return [];
    }
    let arr = [...serType] || [];
    arr.forEach((item) => {
      item.key = item.value;
      item.title = item.label;
      item.type = [...parentType, item.value];
      if (parentTypeLabel !== "") {
        item.typeLabel = parentTypeLabel + "/" + item.label;
      } else {
        item.typeLabel = item.label;
      }
      if (item.hasChildren) {
        item.checkable = false;
        handleServiceType(item.children, item.type, item.typeLabel);
      }
    });
    return arr;
  };

  const getRecordData = async () => {
    try {
      const data: IRecord = await request.get("/dragon/service/record", {
        params: {
          tid,
        },
      });

      if (data.attachments) {
        data.attachments.map((item: any) => (item.name = item.fileName));
      } else {
        data.attachments = [];
      }

      recordRef.current = data;

      // 初始化服务状态及预约时间
      setStatusShow(data.status);
      setTimeShow(data.startTime);

      // 表单初始化数据
      setValue("remark", data.remark);
      setValue("serviceId", data.serviceId);

      // 初始化远程诊断结果和解决方案描述
      setValue("solutionDesc", data.solutionDesc);

      //初始化问题是否解决
      setProblemSolved(data.problemSolved);

      attachListRef.current = data.attachments || [];
      setAttachList(data.attachments || []);

      typeRef.current = data.type ? JSON.parse(data.type) : [];
      typeLabelRef.current = data.typeLabel ? JSON.parse(data.typeLabel) : [];

      // 处理服务选项默认选中
      if (data.type) {
        const typeArr = JSON.parse(data.type);
        const selectedTypeValue: any = [];
        typeArr.map((item) => {
          selectedTypeValue.push(item.slice(-1)[0]);
        });
        setCheckedServiceTypeList(selectedTypeValue);
      }

      initCacheRecord();
    } catch (err) {
      console.log(11111, err);
      setError(true);
    } finally {
      setInitLoading(false);
    }
  };

  const initCacheRecord = () => {
    if (cacheTimerRef.current) {
      window.clearInterval(cacheTimerRef.current);
    }

    cacheTimerRef.current = window.setInterval(() => {
      handleCacheRecord();
    }, CACHE_DELAY);
  };

  const formatStatus = (status: string | number) => {
    const cur = serviceStatus.find((item) => item.value === status);
    return cur?.label || "--";
  };

  const handleBeforeUpload = (files) => {
    const filesArr = Array.from(files) as any[];
    if (filesArr.length + attachList.length > ALLOW_FILE_NUMS) {
      message.warning(`最多允许上传 ${ALLOW_FILE_NUMS} 个文件`);
      return false;
    }

    // 检查文件格式
    for (let fileIndex = 0; fileIndex < filesArr.length; fileIndex++) {
      const indexExt = filesArr[fileIndex]?.name?.lastIndexOf(".");
      if (indexExt > -1) {
        const ext = filesArr[fileIndex]?.name?.substr(indexExt + 1);
        if (!ALLOW_FILE_TYPE.includes(ext)) {
          message.warning(`仅允许上传 ${ALLOW_FILE_TYPE} 格式的文件`);
          return false;
        }
      } else {
        message.warning(`仅允许上传 ${ALLOW_FILE_TYPE} 格式的文件`);
        return false;
      }

      if (filesArr[fileIndex]?.size > FILE_MAX_SIZE) {
        message.warning(`只能上传小于 ${FILE_MAX_MB} MB的文件`);
        return false;
      }
    }

    setUploadLoading(true);
    return true;
  };

  const handleUploadSuccess = (res) => {
    setUploadLoading(false);
    attachListRef.current = [...attachList, ...res];
    setAttachList([...attachList, ...res]);
  };

  const handleUploadError = (msg) => {
    setUploadLoading(false);
    message.error("上传失败，请重试");
  };

  const handleRemoveAttach = (index) => {
    const tmp = [...attachList];
    tmp.splice(index, 1);

    attachListRef.current = [...tmp];
    setAttachList([...tmp]);
  };
  const handleRemoveSelectedType = (e, index) => {
    e.stopPropagation();

    const tmpType = [...typeRef.current];
    const tmpTypeLabel = [...typeLabelRef.current];
    const tmpCheckList = [...checkedServiceTypeList];
    const tmpSelectList = [...selectServiceTypeList];

    tmpType.splice(index, 1);
    tmpTypeLabel.splice(index, 1);
    tmpCheckList.splice(index, 1);

    typeRef.current = tmpType;
    typeLabelRef.current = tmpTypeLabel;
    setCheckedServiceTypeList(tmpCheckList);
    setSelectServiceTypeList(tmpSelectList);
  };

  // 判断是否为需要的服务id
  const needServiceId = useMemo(() => {
    let label = 0;
    selectServiceTypeList.map((item) => {
      if (item.remark) {
        const { requireServiceId } = JSON.parse(item.remark);
        if (requireServiceId) {
          label = 1;
          return;
        }
      }
    });

    return label === 1;
  }, [selectServiceTypeList]);

  const handleCacheRecord = async () => {
    try {
      const { startTime, status }: ICacheRes = await request.post(
        "/dragon/service/record/cache",
        {
          ...recordRef.current,
          serviceId:
            (getValues("serviceId") && getValues("serviceId").trim()) || "",
          remark: (getValues("remark") && getValues("remark").trim()) || "",
          solutionDesc:
            (getValues("solutionDesc") && getValues("solutionDesc").trim()) ||
            "",
          type: JSON.stringify(typeRef.current),
          typeLabel: JSON.stringify(typeLabelRef.current),
          attachments: attachListRef.current,
          tid,
          problemSolved: problemSolvedRef.current,
          remoteDiagnosisRes: remoteDiagnosis,
        }
      );
      setStatusShow(status);
      setTimeShow(startTime);
    } catch (err) {
      console.log(err);
    }
  };

  // 返回选中的服务类型
  const handleSelect = (selectedKeys, e) => {
    if (selectedKeys.length > MAX_SERVICE_TYPE_NUM) {
      message.warning(`最多选${MAX_SERVICE_TYPE_NUM}项`);
      return;
    }

    setCheckedServiceTypeList(selectedKeys);

    let typeArr: any = [];
    let typeLabelArr: any = [];

    e.checkedNodes.map((item) => {
      typeArr.push(item.type);
      typeLabelArr.push(item.typeLabel);
    });

    typeRef.current = typeArr;
    typeLabelRef.current = typeLabelArr;

    setSelectServiceTypeList(e.checkedNodes);
  };

  const handleCommit = async () => {
    if (!checkForm() || commitLoading) {
      return;
    }
    try {
      setCommitLoading(true);
      await request.post("/dragon/service/record/submit", {
        ...recordRef.current,
        serviceId:
          (getValues("serviceId") && getValues("serviceId").trim()) || "",
        remark: (getValues("remark") && getValues("remark").trim()) || "",
        solutionDesc:
          (getValues("solutionDesc") && getValues("solutionDesc").trim()) || "",
        type: JSON.stringify(typeRef.current),
        typeLabel: JSON.stringify(typeLabelRef.current),
        attachments: attachListRef.current,
        problemSolved,
        tid,
        remoteDiagnosisRes: remoteDiagnosis,
      });

      history.push("/success");
      setCommitLoading(false);
    } catch (err: any) {
      setCommitLoading(false);
    }
  };

  const checkForm = () => {
    if (typeLabelRef.current.length === 0) {
      message.error("请选择服务类型");
      return false;
    }

    // watchserviceid有可能为null或者undefined
    if (needServiceId && (!watchServiceId || !watchServiceId.trim())) {
      setValue("serviceId", "");
      message.error("请填写预约单号");
      return false;
    }

    console.log(11111, watchRemark, watchSolutionDesc);
    if (!watchRemark || watchRemark.trim() === "") {
      setValue("remark", "");
      message.error("请填写问题描述");
      return false;
    }
    if (!watchSolutionDesc || watchSolutionDesc.trim() === "") {
      setValue("solutionDesc", "");
      message.error("请描述解决方案");
      return false;
    }

    if (!problemSolved || problemSolved == 'unknown') {
      message.error("请选择问题是否解决");
      return false;
    }

    return true;
  };

  const PickerHeader = () => {
    return (
      <div className={styles.pickerHeader}>
        <LeftOutlined
          onClick={() => {
            notAllowMove.disable();
            setShowServiceItemPicker(false);
          }}
        />
        <p className={styles.pickerTitle}>
          选择服务类型
          <span
            className={styles.tip}
          >{`[最多选${MAX_SERVICE_TYPE_NUM}项]`}</span>
        </p>
      </div>
    );
  };

  const handleReload = () => {
    setError(false);
    setInitLoading(true);
    getRecordData();
    getConfigData();
  };

  return (
    <div className={styles.pageWraps}>
      <PageTabs></PageTabs>
      <div className={styles.contentWrapper}>
        {error ? (
          <Result
            subTitle="数据加载失败，请稍后重试"
            extra={
              <Button type="primary" onClick={handleReload}>
                重新加载
              </Button>
            }
          />
        ) : (
          <Spin spinning={initLoading}>
            <div className={styles.pageContent}>
              <FormItem label="服务状态">
                <div className={styles.staticContent}>
                  {formatStatus(statusShow)}
                </div>
              </FormItem>
              <FormItem label="开始时间">
                <div className={styles.staticContent}>
                  {formatTimestamp(timeShow)}
                </div>
              </FormItem>
              <FormItem label="服务类型" required>
                <div className={styles.serviceType}>
                  <div
                    className={styles.picker}
                    onClick={() => {
                      notAllowMove.enable();
                      setShowServiceItemPicker(true);
                    }}
                  >
                    <div className={styles.serviceItem}>
                      {typeLabelRef.current.length > 0 && (
                        <div className={styles.serviceTypeWrap}>
                          {typeLabelRef.current.map((item, itemIndex) => {
                            return (
                              <div
                                className={styles.serviceTypeItem}
                                key={itemIndex}
                              >
                                <p className={styles.name}>{item}</p>
                                <div
                                  className={styles.delIcon}
                                  onClick={(event) =>
                                    handleRemoveSelectedType(event, itemIndex)
                                  }
                                ></div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {typeLabelRef.current.length === 0 && (
                        <p className={styles.placeholder}>请选择服务类型</p>
                      )}
                    </div>
                    <div className={styles.pickerIcon}></div>
                  </div>
                </div>
              </FormItem>
              <FormItem label="预约单号" required={needServiceId}>
                <input
                  className={styles.inputCell}
                  placeholder="请输入预约单号"
                  maxLength={60}
                  {...register("serviceId", { required: true })}
                />
              </FormItem>
              <FormItem label="问题描述" required>
                <textarea
                  className={styles.textareaCell}
                  placeholder="请输入问题描述"
                  maxLength={500}
                  rows={5}
                  {...register("remark", {
                    required: true,
                    validate: (value) => value.trim() !== "",
                  })}
                />
              </FormItem>
              <FormItem label="问题是否解决" required>
                <Radio.Group
                  onChange={(event) => {
                    setProblemSolved(event.target.value);
                  }}
                  value={problemSolved}
                >
                  <Radio value="yes">已解决</Radio>
                  <Radio value="no">未解决</Radio>
                </Radio.Group>
              </FormItem>

              <FormItem label="解决方案描述" required>
                <textarea
                  className={styles.textareaCell}
                  placeholder="请描述解决方案，如涉及远程诊断，也需要填写诊断的详情"
                  maxLength={1024}
                  rows={5}
                  {...register("solutionDesc", {
                    required: true,
                    validate: (value) => value.trim() !== "",
                  })}
                />
              </FormItem>
              <div className={styles.radioWrapper}>
                <FormItem label="远程诊断结果（如有）">
                  <Radio.Group
                    onChange={(event) => {
                      setRemoteDiagnosis(event.target.value);
                    }}
                    value={remoteDiagnosis}
                  >
                    <Radio value={1}>有故障</Radio>
                    <Radio value={2}>无故障</Radio>
                    <Radio value={3}>无法判断</Radio>
                  </Radio.Group>
                </FormItem>
              </div>
              <FormItem
                label="上传附件"
                labelTip={`请上传 ${ALLOW_FILE_TYPE} 格式的文件，单个文件不超过10MB, 最多上传 ${ALLOW_FILE_NUMS} 个文件`}
              >
                <Upload
                  disabled={uploadLoading}
                  accept={ALLOW_FILE_TYPE}
                  multiple={true}
                  beforeUpload={handleBeforeUpload}
                  onSuccess={handleUploadSuccess}
                  onError={handleUploadError}
                >
                  <div className={styles.uploadBtn}>
                    {uploadLoading ? (
                      <p className={styles.txt}>上传中...</p>
                    ) : (
                      <>
                        <div className={styles.addIcon}>+</div>
                        <p className={styles.txt}>点击上传附件</p>
                      </>
                    )}
                  </div>
                </Upload>
              </FormItem>
              <FormItem label=" ">
                <div className={styles.attachWrap}>
                  {attachList.map((attachItem, attachIndex) => {
                    return (
                      <div className={styles.attachItem} key={attachIndex}>
                        <p className={styles.name}>{attachItem.fileName}</p>
                        <div
                          className={styles.delIcon}
                          onClick={() => handleRemoveAttach(attachIndex)}
                        ></div>
                      </div>
                    );
                  })}
                </div>
              </FormItem>
            </div>
          </Spin>
        )}
        {!error && (
          <div className={styles.submitBtnWrap}>
            <div className={styles.submitBtn} onClick={handleCommit}>
              {commitLoading ? "提交中..." : "提交"}
            </div>
          </div>
        )}
      </div>
      {showServiceItemPicker && (
        <Drawer
          title={<PickerHeader></PickerHeader>}
          placement="right"
          visible={showServiceItemPicker}
          getContainer={false}
          width="90%"
          onClose={() => {
            notAllowMove.disable();
            setShowServiceItemPicker(false);
          }}
          closable={false}
        >
          <Tree
            treeData={serviceType}
            defaultExpandAll={true}
            defaultCheckedKeys={checkedServiceTypeList}
            showLine={{ showLeafIcon: false }}
            selectable={false}
            onCheck={handleSelect}
            checkable
          ></Tree>
        </Drawer>
      )}
    </div>
  );
}
